import { Link } from "react-router-dom";
import { DIMENSIONS } from "@/data/dimensions";
import {
  ChevronDown,
  Sparkles,
  ShieldCheck,
  Star,
  Quote,
  BookOpen,
  KeyRound,
  Users,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PAYMENT_LINK = "https://rzp.io/rzp/wpYnWgl";

// Plain stacked sections in normal document flow (native scroll, no 3D scene).
export default function Overlay() {
  const scrollToCTA = () => {
    const el = document.scrollingElement || document.documentElement;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  return (
    <div className="sans" style={{ pointerEvents: "auto", color: "#F4F6F0" }}>
      {/* =============== SECTION A: Hero =============== */}
      <section
        data-testid="section-hero"
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          textAlign: "left",
          padding: "0 1.5rem",
          position: "relative",
          backgroundImage: "url(/images/hero-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center 30%",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(3,3,10,0.4) 0%, rgba(3,3,10,0.15) 30%, rgba(3,3,10,0) 55%), linear-gradient(180deg, rgba(3,3,10,0.85) 0%, rgba(3,3,10,0.15) 14%, rgba(3,3,10,0.1) 55%, rgba(3,3,10,0.75) 88%, rgba(3,3,10,1) 100%), linear-gradient(90deg, rgba(3,3,10,1) 0%, rgba(3,3,10,0) 6%, rgba(3,3,10,0) 94%, rgba(3,3,10,1) 100%)",
          }}
        />
        <div style={{ maxWidth: 560, position: "relative", zIndex: 1, textAlign: "left", marginRight: "auto", marginLeft: "4vw" }}>
          <div className="subtitle-caps" style={{ marginBottom: 22 }}>
            <Sparkles size={12} style={{ display: "inline", marginRight: 8, verticalAlign: "-2px" }} />
            The map that was never made
          </div>
          <h1
            className="serif hero-headline"
            style={{
              fontSize: "clamp(1.9rem, 4vw, 3.2rem)",
              fontWeight: 500,
              lineHeight: 1.12,
              letterSpacing: "-0.01em",
              margin: "0 0 1.4rem 0",
              textShadow: "0 4px 24px rgba(0,0,0,0.7)",
            }}
          >
            Every Child Gets a Kundali.
            <br />
            <em style={{ color: "#D4AF37", fontStyle: "italic", fontWeight: 400 }}>
              Nobody Gets One For Their Mind.
            </em>
          </h1>
          <p
            style={{
              fontSize: "clamp(0.92rem, 1.2vw, 1.02rem)",
              maxWidth: 460,
              margin: "0 0 2.4rem",
              color: "#E4E4EA",
              lineHeight: 1.65,
              fontWeight: 300,
              textShadow: "0 2px 14px rgba(0,0,0,0.8)",
            }}
          >
            Your stars were mapped. Your numbers were mapped. But the one thing actually deciding
            what you do with both — your mind — was never read. That's usually where the real
            answer was hiding.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "flex-start", flexWrap: "wrap" }}>
            <a
              data-testid="hero-cta-pay"
              href={PAYMENT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
              style={{ textDecoration: "none" }}
            >
              Get Your Mind Kundali
              <Sparkles size={16} />
            </a>
            <button
              data-testid="hero-cta-scroll"
              onClick={scrollToCTA}
              className="ghost-button"
            >
              Learn more
              <ChevronDown size={16} />
            </button>
          </div>
          <p style={{ marginTop: "1.25rem", fontSize: "0.85rem", color: "#A0A0B0", textShadow: "0 2px 10px rgba(0,0,0,0.8)" }}>
            Already purchased?{" "}
            <Link to="/login" style={{ color: "#D4AF37", textDecoration: "none" }}>
              Log in
            </Link>
          </p>
        </div>
      </section>

      {/* =============== SECTION B: Problem =============== */}
      <section
        data-testid="section-problem"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8rem 6vw",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <style>{`
          @media (max-width: 860px) {
            .problem-bg-img { display: none; }
          }
        `}</style>
        <img
          src="/images/problem-chart.png"
          alt=""
          className="problem-bg-img"
          style={{
            position: "absolute",
            top: -40,
            left: 0,
            width: 520,
            maxWidth: "50vw",
            zIndex: 0,
            pointerEvents: "none",
            userSelect: "none",
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.55) 20%, black 42%)",
            maskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.55) 20%, black 42%)",
          }}
        />
        <img
          src="/images/problem-face.png"
          alt=""
          className="problem-bg-img"
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 600,
            maxWidth: "56vw",
            zIndex: 0,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
        <div className="glass-panel" style={{ maxWidth: 620, padding: "2.4rem 2.2rem", borderRadius: 18, position: "relative", zIndex: 2 }}>
          <div className="subtitle-caps" style={{ marginBottom: 14 }}>
            <BookOpen size={12} style={{ display: "inline", marginRight: 8, verticalAlign: "-2px" }} />
            The problem
          </div>
          <h2
            className="serif"
            style={{
              fontSize: "clamp(1.7rem, 3.2vw, 2.4rem)",
              margin: "0 0 1.4rem",
              color: "#F4F6F0",
              lineHeight: 1.3,
              fontWeight: 500,
            }}
          >
            In almost every Indian household, a child's kundali gets made within days of being born.
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem", color: "#C0C0CC", lineHeight: 1.8, fontSize: "1.05rem" }}>
            <p style={{ margin: 0 }}>
              Their numbers get calculated too — the number that shapes their name, their path,
              their timing. Two maps, made for every single person, without anyone asking whether
              they "believe" in it first. It's just what you do. Because it's understood that a
              life can be read.
            </p>
            <p style={{ margin: 0 }}>But think about it logically for a second.</p>
            <p style={{ margin: 0 }}>
              Your kundali is like the weather — it tells you the conditions. Your numbers are
              like the terrain — they tell you the shape of the path. But neither one of them is
              driving. The thing actually deciding whether you move forward, hold back, chase
              something, or quietly avoid it — that's your mind.
            </p>
            <p style={{ margin: 0 }}>And your mind is the one part of the whole system nobody ever mapped.</p>
            <p style={{ margin: 0 }}>
              So when something in life feels stuck, or the same problem keeps returning no matter
              how many times you've "fixed" it — most people blame the timing. The stars. The
              effort. The luck.
            </p>
            <p style={{ margin: 0 }}>Rarely does anyone check the one thing that was actually running the whole time.</p>
            <p style={{ margin: 0, color: "#D4AF37", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: "1.15rem" }}>
              That's usually where the real answer was sitting. Not because the problem was
              complicated — but because the one map that could've shown it simply never existed.
            </p>
          </div>
        </div>
      </section>

      {/* =============== SECTION C: Explanation — the missing map =============== */}
      <section
        data-testid="section-explanation"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8rem 6vw",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <img
          src="/images/missing-map-bg.png"
          alt=""
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(1300px, 130%)",
            maxWidth: "none",
            zIndex: 0,
            opacity: 0.7,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
        <div style={{ maxWidth: 1100, width: "100%", position: "relative", zIndex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="subtitle-caps" style={{ marginBottom: 14 }}>
              <KeyRound size={12} style={{ display: "inline", marginRight: 8, verticalAlign: "-2px" }} />
              The missing map
            </div>
            <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3.6vw, 2.7rem)", margin: "0 0 1.2rem", fontWeight: 500, lineHeight: 1.15 }}>
              The one thing
              <br />
              <span style={{ color: "#D4AF37", fontStyle: "italic" }}>that was always missing.</span>
            </h2>
            <p style={{ color: "#A0A0B0", fontSize: "1.05rem", lineHeight: 1.65, maxWidth: 720, margin: "0 auto" }}>
              If your birth kundali reads the sky, and your numerology reads your name and number
              — Mind Kundali reads the one thing that was always missing: the actual patterns
              running your mind. Because your kundali can say the timing is right — but if your
              mind is quietly working against you, timing alone won't move you forward.
            </p>
          </div>

          <p className="serif" style={{ textAlign: "center", color: "#D4AF37", fontStyle: "italic", fontSize: "1.15rem", margin: "0 0 30px" }}>
            Mind Kundali shows you three things:
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18 }}>
            {[
              {
                img: "/images/missing-map-icon-1.png",
                head: "What your mind is consciously working toward",
                body: "And whether it's actually aligned with it — or quietly working against itself without you noticing.",
              },
              {
                img: "/images/missing-map-icon-2.png",
                head: "What it's unconsciously resisting",
                body: "The pattern behind the choices you keep almost making, and don't.",
              },
              {
                img: "/images/missing-map-icon-3.png",
                head: "What to do next",
                body: "Direction built from what your own mind is already doing, finally put into words.",
              },
            ].map((it, i) => (
              <div
                key={i}
                data-testid={`explanation-card-${i + 1}`}
                className="glass-panel"
                style={{ padding: "1.8rem 1.6rem", borderRadius: 16, display: "flex", flexDirection: "column", gap: 12 }}
              >
                <img src={it.img} alt="" style={{ height: 78, width: "auto", margin: "0 auto 6px", display: "block" }} />
                <h3 className="serif" style={{ margin: 0, fontSize: "1.35rem", color: "#F4F6F0", fontWeight: 500, lineHeight: 1.25 }}>
                  {it.head}
                </h3>
                <p style={{ margin: 0, color: "#A0A0B0", fontSize: "0.95rem", lineHeight: 1.55 }}>
                  {it.body}
                </p>
              </div>
            ))}
          </div>

          <p className="serif" style={{ textAlign: "center", marginTop: 40, fontSize: "clamp(1.05rem, 1.8vw, 1.3rem)", color: "#D4AF37", fontStyle: "italic", lineHeight: 1.5 }}>
            Your kundali is your sky. Your numbers are your path. Your mind is what actually walks
            it. It was always the missing map.
          </p>
          <img
            src="/images/missing-map-divider.png"
            alt=""
            style={{ display: "block", width: "min(480px, 70%)", height: "auto", margin: "22px auto 18px", opacity: 0.9 }}
          />
          <p style={{ textAlign: "center", marginTop: 0, color: "#A0A0B0", fontSize: "0.95rem", maxWidth: 600, marginLeft: "auto", marginRight: "auto" }}>
            Once you see your mind clearly, most of what felt complicated turns out to have a
            simple answer.
          </p>
        </div>
      </section>

      {/* =============== SECTION D: The 12 Chambers (the patterns inside your mind) =============== */}
      <section
        data-testid="section-chambers-intro"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4rem 1.5rem 2rem",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 640 }}>
          <div className="subtitle-caps" style={{ marginBottom: 14 }}>Inside the mind</div>
          <h2 className="serif" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", margin: 0, fontWeight: 500, lineHeight: 1.2 }}>
            Twelve rooms.
            <span style={{ color: "#D4AF37", fontStyle: "italic" }}> The same twelve every mind has.</span>
          </h2>
          <p style={{ color: "#A0A0B0", marginTop: 14, fontSize: "0.95rem", lineHeight: 1.6 }}>
            Your Mind Kundali reads how each one is behaving — which are lit, which are guarded,
            which are the ones quietly steering everything.
          </p>
        </div>
      </section>

      {DIMENSIONS.map((d, idx) => (
        <section
          key={d.id}
          data-testid={`chamber-${d.id}`}
          style={{
            height: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: idx % 2 === 0 ? "flex-start" : "flex-end",
            padding: "0 6vw",
          }}
        >
          <div
            className="glass-panel"
            style={{
              maxWidth: 460,
              padding: "1.6rem 1.8rem",
              borderRadius: 14,
              textAlign: idx % 2 === 0 ? "left" : "right",
              borderColor: `${d.color}55`,
            }}
          >
            <div
              className="subtitle-caps"
              style={{ color: d.color, marginBottom: 10, display: "flex", alignItems: "center", gap: 8, justifyContent: idx % 2 === 0 ? "flex-start" : "flex-end" }}
            >
              <span className="pulse-dot" style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, boxShadow: `0 0 12px ${d.color}` }} />
              Room {String(d.id).padStart(2, "0")} · {d.label}
            </div>
            <h3
              className="serif"
              style={{ fontSize: "clamp(1.3rem, 2.3vw, 1.7rem)", margin: "0 0 0.6rem", color: "#F4F6F0", fontWeight: 500, lineHeight: 1.2 }}
            >
              {d.name}
            </h3>
            <p style={{ color: "#C8C8D2", fontSize: "0.98rem", lineHeight: 1.55, fontStyle: "italic", margin: 0 }}>
              "{d.insight}"
            </p>
          </div>
        </section>
      ))}

      {/* Mid-page payment CTA */}
      <section
        data-testid="section-cta-mid-chambers"
        style={{ display: "flex", justifyContent: "center", padding: "3rem 1.5rem" }}
      >
        <a
          data-testid="chambers-cta-pay"
          href={PAYMENT_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="cta-button"
          style={{ textDecoration: "none" }}
        >
          Read My Mind Kundali
          <Sparkles size={16} />
        </a>
      </section>

      {/* =============== SECTION E: Benefits — what you walk away with =============== */}
      <section
        data-testid="section-benefits"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          padding: "8rem 6vw",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="subtitle-caps" style={{ marginBottom: 12 }}>🎁 Benefits</div>
            <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", margin: 0, fontWeight: 500, lineHeight: 1.15 }}>
              What you <span style={{ color: "#D4AF37", fontStyle: "italic" }}>walk away with.</span>
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
            {[
              { emoji: "🧩", head: "A clear reason why certain problems in your life keep repeating", body: "Not vague reasons — the actual pattern." },
              { emoji: "🗝️", head: 'The realization that most of what felt like a "big, complicated life problem" has a simple fix', body: "You just never had the map to see it." },
              { emoji: "🕊️", head: "Relief from blaming yourself, your luck, or your timing", body: "For things that were never really about any of that." },
              { emoji: "🧭", head: "A next step that finally makes sense", body: "Because it's based on how your mind actually works, not a guess." },
              { emoji: "✨", head: "The rare experience of understanding yourself", body: "As clearly as you understand your own kundali." },
            ].map((b, i) => (
              <div
                key={i}
                data-testid={`benefit-${i + 1}`}
                className="glass-panel"
                style={{ padding: "1.6rem 1.4rem", borderRadius: 14, display: "flex", flexDirection: "column", gap: 10 }}
              >
                <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>{b.emoji}</span>
                <h3 className="serif" style={{ margin: 0, fontSize: "1.15rem", color: "#F4F6F0", fontWeight: 500, lineHeight: 1.3 }}>{b.head}</h3>
                <p style={{ margin: 0, color: "#A0A0B0", fontSize: "0.95rem", lineHeight: 1.6 }}>{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =============== SECTION F: How it works =============== */}
      <section
        data-testid="section-how"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8rem 6vw",
        }}
      >
        <div style={{ maxWidth: 1100, width: "100%" }}>
          <div className="subtitle-caps" style={{ marginBottom: 14, textAlign: "center" }}>How it works</div>
          <h2 className="serif" style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.7rem)", margin: "0 0 3rem", textAlign: "center", fontWeight: 500, lineHeight: 1.15 }}>
            Three steps to read <span style={{ color: "#D4AF37", fontStyle: "italic" }}>your inner chart</span>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
            {[
              { emoji: "📜", num: "01", title: "Answer honestly", body: "A short, guided self-assessment. No overthinking — your first instinct is usually the truest one." },
              { emoji: "✦", num: "02", title: "Your Mind Kundali is generated", body: "We read the pattern underneath your answers, the same way your birth kundali reads the position of the planets." },
              { emoji: "📖", num: "03", title: "Receive your reading", body: "A personal, plain-language breakdown — what your mind is working toward, what it's resisting, and the simple next step from here." },
            ].map((step, i) => (
              <div
                key={i}
                data-testid={`how-step-${i + 1}`}
                className="glass-panel"
                style={{
                  padding: "1.8rem 1.6rem",
                  borderRadius: 16,
                  minHeight: 220,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 44, height: 44, borderRadius: 12,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: "rgba(212, 175, 55, 0.1)",
                      border: "1px solid rgba(212, 175, 55, 0.25)",
                      fontSize: 20,
                    }}
                  >
                    {step.emoji}
                  </div>
                  <span className="subtitle-caps">Step {step.num}</span>
                </div>
                <h3 className="serif" style={{ margin: 0, fontSize: "1.4rem", color: "#F4F6F0", fontWeight: 500, lineHeight: 1.2 }}>
                  {step.title}
                </h3>
                <p style={{ margin: 0, color: "#A0A0B0", fontSize: "0.95rem", lineHeight: 1.6 }}>
                  {step.body}
                </p>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "3rem" }}>
            <a
              data-testid="how-cta-pay"
              href={PAYMENT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
              style={{ textDecoration: "none" }}
            >
              Get Your Mind Kundali
              <Sparkles size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* =============== SECTION G: Who it's for =============== */}
      <section
        data-testid="section-who"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          padding: "8rem 6vw",
        }}
      >
        <style>{`
          .who-grid { display:grid; grid-template-columns:1fr 1fr; gap:22px; }
          .who-card { display:flex; align-items:stretch; height:300px; background:linear-gradient(135deg, rgba(12,12,20,0.92), rgba(3,3,9,0.96)); border:1px solid rgba(212,175,55,0.14); border-radius:16px; overflow:hidden; }
          .who-card-text { flex:0 0 42%; padding:1.8rem 1.7rem; display:flex; flex-direction:column; justify-content:center; }
          .who-num-row { display:flex; align-items:center; gap:12px; margin-bottom:16px; }
          .who-num { font-family:'Cormorant Garamond',serif; font-style:italic; color:#D4AF37; font-size:1.3rem; line-height:1; flex-shrink:0; }
          .who-rule { width:44px; height:1px; background:rgba(212,175,55,0.45); flex-shrink:0; }
          .who-heading { font-size:1.45rem; font-weight:500; color:#F4F6F0; line-height:1.26; margin:0 0 14px; }
          .who-body { color:#A8A8B4; font-size:0.85rem; line-height:1.6; margin:0; }
          .who-card-img { flex:1 1 58%; position:relative; overflow:hidden; }
          .who-card-img img { position:absolute; inset:0; width:100%; height:100%; object-fit:contain; object-position:right center; }
          @media (max-width: 860px) {
            .who-grid { grid-template-columns:1fr; }
            .who-card { flex-direction:column; height:auto; }
            .who-card-text { flex:none; padding:1.8rem 1.6rem 1rem; }
            .who-card-img { flex:none; height:230px; }
            .who-card-img img { object-position:center center; }
          }
        `}</style>
        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="subtitle-caps" style={{ marginBottom: 12 }}>
              <Users size={12} style={{ display: "inline", marginRight: 8, verticalAlign: "-2px" }} />
              Who it's for
            </div>
            <h2 className="serif" style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", margin: 0, fontWeight: 500, lineHeight: 1.15 }}>
              If any of these sound like you,
              <br />
              <span style={{ color: "#D4AF37", fontStyle: "italic" }}>this was made for you.</span>
            </h2>
          </div>

          <div className="who-grid">
            {[
              {
                num: "01",
                img: "/images/who-book.jpg",
                heading: "You already believe a life can be read and understood.",
                body: "You've had your kundali made, checked your numbers, or explored these systems before making big decisions.",
              },
              {
                num: "02",
                img: "/images/who-ring.jpg",
                heading: "You've felt the same problem repeat for years.",
                body: "Different situations, same pattern. You know something's off, but you've never found a clear reason why.",
              },
              {
                num: "03",
                img: "/images/who-wire.jpg",
                heading: "You're tired of complicated explanations.",
                body: "You've read, watched, tried different methods — but most answers just create more confusion. You want something simple and clear.",
              },
              {
                num: "04",
                img: "/images/who-head.jpg",
                heading: "You've never had your mind mapped.",
                body: "You may have explored your stars, your numbers, maybe even therapy — but nobody has shown you a complete map of how your mind actually works.",
              },
            ].map((w, i) => (
              <div key={i} data-testid={`who-card-${i + 1}`} className="who-card">
                <div className="who-card-text">
                  <div className="who-num-row">
                    <span className="who-num">{w.num}</span>
                    <span className="who-rule" />
                  </div>
                  <h3 className="who-heading serif">{w.heading}</h3>
                  <p className="who-body">{w.body}</p>
                </div>
                <div className="who-card-img">
                  <img src={w.img} alt="" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =============== SECTION H: Testimonials (kept minimal, real placeholders) =============== */}
      <section
        data-testid="section-trust"
        style={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          padding: "6rem 6vw",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="subtitle-caps" style={{ marginBottom: 12 }}>Early readers</div>
            <h2 className="serif" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", margin: 0, fontWeight: 500, lineHeight: 1.15 }}>
              A different kind of clarity.
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {[
              { q: "It named something I'd been circling for two years — in one line.", a: "— Reader, Mumbai" },
              { q: "Felt less like a quiz. More like finally being read.", a: "— Reader, Bengaluru" },
              { q: "I didn't know a reading could be this specific and this quiet at the same time.", a: "— Reader, Delhi" },
            ].map((t, i) => (
              <div key={i} className="glass-panel" data-testid={`testimonial-${i}`} style={{ padding: "1.6rem 1.4rem", borderRadius: 14 }}>
                <Quote size={18} style={{ color: "#D4AF37", marginBottom: 12 }} />
                <p className="serif" style={{ margin: "0 0 12px", fontSize: "1.05rem", color: "#F4F6F0", fontStyle: "italic", lineHeight: 1.5, fontWeight: 500 }}>
                  "{t.q}"
                </p>
                <div style={{ color: "#A0A0B0", fontSize: "0.82rem", letterSpacing: "0.06em" }}>{t.a}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "3rem" }}>
            <a
              data-testid="trust-cta-pay"
              href={PAYMENT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
              style={{ textDecoration: "none" }}
            >
              Get Your Mind Kundali — ₹899
              <Sparkles size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* =============== SECTION I: FAQ =============== */}
      <section
        data-testid="section-faq"
        style={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          padding: "6rem 6vw",
        }}
      >
        <div style={{ maxWidth: 820, margin: "0 auto", width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div className="subtitle-caps" style={{ marginBottom: 12 }}>Questions people ask</div>
            <h2 className="serif" style={{ fontSize: "clamp(1.7rem, 3.2vw, 2.4rem)", margin: 0, fontWeight: 500, lineHeight: 1.15 }}>
              Answered plainly.
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {[
              {
                q: "How is this different from astrology or numerology?",
                a: "Your kundali and your numbers are fixed the day you're born — they describe the pattern you were given. Mind Kundali reads something different: the pattern actually running inside your mind, right now, deciding what you do with what you were given.",
              },
              {
                q: "Why does this matter so much?",
                a: "Because your mind is the one thing deciding, every day, whether you move toward what's meant for you or away from it. A good kundali with an unread mind is like a good car with nobody checking the engine.",
              },
              {
                q: "Is this therapy or a diagnosis?",
                a: "No. It's a reflective, personal reading — like a chart reading, not a clinical assessment.",
              },
              {
                q: "Why is it ₹899?",
                a: "It's priced like a serious reading, not a gimmick — because a proper reading of your mind deserves the same care as your kundali or numerology reading, not a five-minute quiz.",
              },
              {
                q: "Will this tell me exactly what to do with my life?",
                a: "It won't hand you a script. It'll show you the pattern clearly enough that your next step becomes obvious on its own.",
              },
            ].map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                style={{
                  borderBottom: "1px solid rgba(212, 175, 55, 0.14)",
                }}
              >
                <AccordionTrigger
                  className="serif"
                  style={{
                    color: "#F4F6F0",
                    fontSize: "1.1rem",
                    fontWeight: 500,
                    textAlign: "left",
                    padding: "1.2rem 0",
                  }}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  style={{
                    color: "#A8A8B4",
                    fontSize: "0.98rem",
                    lineHeight: 1.65,
                    paddingBottom: "1.2rem",
                    fontFamily: "'Outfit', sans-serif",
                  }}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* =============== SECTION J: Closing CTA =============== */}
      <section
        data-testid="section-final-cta"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8rem 1.5rem 8rem",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 720, width: "100%" }}>
          <div className="subtitle-caps" style={{ marginBottom: 20 }}>
            <Star size={12} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />
            The third map
          </div>
          <h2
            className="serif hero-headline"
            style={{
              fontSize: "clamp(2rem, 5vw, 3.4rem)",
              lineHeight: 1.1,
              fontWeight: 500,
              margin: "0 0 1.4rem",
            }}
          >
            Your kundali was made the week you were born.
            <br />
            <span style={{ color: "#D4AF37", fontStyle: "italic" }}>Your numbers, before you could even ask why.</span>
          </h2>
          <div style={{ color: "#A0A0B0", fontSize: "1rem", lineHeight: 1.7, margin: "0 auto 2.4rem", maxWidth: 560, display: "flex", flexDirection: "column", gap: "0.8rem" }}>
            <p style={{ margin: 0 }}>The one map that was never made — the one that actually runs the other two — is your mind.</p>
            <p style={{ margin: 0, color: "#F4F6F0", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: "1.15rem" }}>
              Most of what feels complicated about your life has a simple answer sitting inside
              it. You just haven't read it yet.
            </p>
          </div>

          <div
            className="glass-panel"
            style={{
              padding: "2.2rem 1.6rem",
              borderRadius: 22,
              display: "flex",
              flexDirection: "column",
              gap: 16,
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span className="serif" style={{ fontSize: "clamp(2.4rem, 5vw, 3.4rem)", color: "#D4AF37", fontWeight: 500, lineHeight: 1 }}>
                ₹899
              </span>
              <span className="subtitle-caps" style={{ color: "#F4F6F0" }}>Discover your Mind Kundali</span>
            </div>
            <a
              data-testid="cta-checkout-button"
              href={PAYMENT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
              style={{ textDecoration: "none" }}
            >
              Get Your Mind Kundali — ₹899
              <Sparkles size={16} />
            </a>
            <div style={{ display: "flex", gap: 14, color: "#A0A0B0", fontSize: "0.78rem", letterSpacing: "0.08em", flexWrap: "wrap", justifyContent: "center" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <ShieldCheck size={12} /> Secure checkout via Razorpay
              </span>
              <span>·</span>
              <span>Read once. Kept for life.</span>
            </div>
          </div>

          <p style={{ color: "rgba(244, 246, 240, 0.35)", fontSize: "0.75rem", marginTop: 32, letterSpacing: "0.1em" }}>
            © Mind Kundali · Made with quiet intention
          </p>
        </div>
      </section>
    </div>
  );
}
